/* eslint-disable camelcase */
import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_LICENSES,
	GET_SYSTEM_CONFIG,
	UPLOAD_LICENCE_API,
    UPLOAD_DEVICE_LICENCE,
	API_SUCCESS_CODES,
	API_RESPONSE_SUCCESS_STATUS_CODE,
	AUTH_TOKEN_ERROR_STATUS_CODE,
	TOKEN_FAILED,
	LICENSE_DETAILS_POPUP,
	LICENSE_DETAILS_UPDATE_POPUP
} from '../../constants/commonConstants';
import {
	AP_NOTIFICATION,
	AP_NOTIFICATION_FAIL,
	AP_NOTIFICATION_SUCCESS,
	GET_LICENSE_LIST,
	GET_LICENSE_LIST_FAIL,
	GET_LICENSE_LIST_SUCCESS,
	GET_SYSTEM_CONFIG_LIST,
	GET_SYSTEM_CONFIG_LIST_FAIL,
	GET_SYSTEM_CONFIG_LIST_SUCCESS,
	LICENSE_UPDATE_PATCH,
	LICENSE_UPDATE_PATCH_ERROR,
	LICENSE_UPDATE_PATCH_SUCCESS,
	LICENSE_UPDATE_POST,
	LICENSE_UPDATE_POST_FAIL,
	LICENSE_UPDATE_POST_SUCCESS,
	UPLOAD_LICENSE_FAIL,
	UPLOAD_LICENSE_SUCCESS,
	LICENSE_DETAILS_POPUP_REQUEST,
	LICENSE_DETAILS_POPUP_SUCCESS,
	LICENSE_DETAILS_POPUP_FAILURE,
	LICENSE_UPDATE_POPUP_REQUEST,
	LICENSE_UPDATE_POPUP_SUCCESS,
	LICENSE_UPDATE_POPUP_FAILURE
} from '../../constants/licenseManagement/licenseConstants';
import { logout } from '../userActions';
import { decryptData, encryptData } from '../../utils/crypto';

/**
 * Fetches all licenses and dispatches the appropriate Redux actions.
 *
 * This function makes an asynchronous GET request to the server to retrieve license data.
 * It dispatches an action indicating the start of the process, processes the retrieved data,
 * and dispatches success or failure actions based on the result.
 *
 * @return {function} - An asynchronous function that dispatches Redux actions.
 */
export const getAllLicenses = () => async (dispatch) => {
	try {
		dispatch({ type: GET_LICENSE_LIST });

		const config = {
		method: 'get',
		url: `${ENDPOINT_URL}${GET_LICENSES}`,
		maxBodyLength: Infinity,
	};

		let { data } = await axios.request(config);
		const jsonString = decryptData(data);
		data = JSON.parse(jsonString);

		if (
			data &&
			data.status &&
			data.status === AUTH_TOKEN_ERROR_STATUS_CODE
		) {
			dispatch(logout());
			return false
		}

		if (!API_SUCCESS_CODES?.includes(data?.status))  throw Error("Invalid response") ;

		if (data?.data) {
			console.log('data', data.data);
			const expiringSoon = {};
			// Transform the license data into the desired structure
			const convertedArray = Object.entries(data.data)
				.filter(([key]) => key !== 'final_license_dict')
				.map(([_, value]) => {
					const {
						license_id,
						type,
						element: [
							{
								'Access Point': ap_count = null,
								Tdma: tdma = null,
								Switch: switches = null,
							} = {},
						] = [],
						expiry,
						grace,
						is_expired,
						is_grace_period,
						status,
						activated_at,
						feature,
					} = value;

					// calculating largest expiring time 
					if(Object.keys(expiringSoon).length === 0){
						expiringSoon['grace'] = value.grace;
						expiringSoon['license_id'] = value.license_id;
						expiringSoon['type'] = value.type;
					}else if (new Date(value.grace) < new Date(expiringSoon.grace)){
						expiringSoon['grace'] = value.grace;
						expiringSoon['license_id'] = value.license_id;
						expiringSoon['type'] = value.type;
					}
					return {
						license_id,
						type,
						ap_count,
						tdma,
						switches,
						expiry,
						grace,
						is_expired,
						is_grace_period,
						status,
						activated_at,
						feature,
					};
				});

			const licenseObject = JSON.parse(
				data.data.final_license_dict.replace(/'/g, '"')
			)?.data;
			const licenseInfo = {
				issued_to: licenseObject?.register_to,
				access_points: licenseObject?.element_count?.['Access Point'],
				switches: licenseObject?.element_count?.['Switch'],
				groups:
					parseInt(licenseObject.retentions?.ap_group || 0) ,
					// + parseInt(licenseObject?.retentions?.ubr_group || 0),
				roles: licenseObject?.retentions?.roles,
				users: licenseObject?.retentions?.users,
				totalLicenses: convertedArray.length,
				activeLicenses: convertedArray.filter((item)=>!item.is_expired && item.status == 'Active').length,
			};

			dispatch({
				type: GET_LICENSE_LIST_SUCCESS,
				payload: { list: convertedArray, info: licenseInfo, expiringSoon },
			});
		}
	} catch (error) {
		console.error('Error fetching licenses:', error);
		dispatch({
			type: GET_LICENSE_LIST_FAIL,
			payload: error.message || GET_LICENSE_LIST_FAIL,
		});
	}
};

export const getSystemConfig = () => async (dispatch, getState) => {
	try {
		dispatch({ type: GET_SYSTEM_CONFIG_LIST });

		const config = {
			method: 'get',
		url: `${ENDPOINT_URL}${GET_SYSTEM_CONFIG}`,
		maxBodyLength: Infinity,
	};

		let { data } = await axios.request(config);
		const jsonString = decryptData(data);
		data = JSON.parse(jsonString);
		if (
			data &&
			data.status &&
			data.status === AUTH_TOKEN_ERROR_STATUS_CODE
		) {
			dispatch(logout());
			return false
		}

		if (data?.data || API_SUCCESS_CODES?.includes(data?.status)) {
			dispatch({
				type: GET_SYSTEM_CONFIG_LIST_SUCCESS,
				payload: data.data,
			});
			return data.data;
		}
		if(data.status === 401){
						dispatch({
							type: GET_SYSTEM_CONFIG_LIST_FAIL,
							payload: data?.errors || GET_SYSTEM_CONFIG_LIST_FAIL,
						});
						dispatch(logout());
					}
		 else {
			dispatch({
				type: GET_SYSTEM_CONFIG_LIST_FAIL,
				payload: data?.errors || GET_SYSTEM_CONFIG_LIST_FAIL,
			});
		}
	} catch (error) {
		console.error('Error fetching licenses:', error);
		dispatch({
			type: GET_SYSTEM_CONFIG_LIST_FAIL,
			payload: error.message || GET_SYSTEM_CONFIG_LIST_FAIL,
		});
	}
};

export const uploadLicense = (uploadData, isDeviceLicense = false) => async (dispatch, getState) => {
	try {
		dispatch({ type: GET_LICENSE_LIST });


		const config = {
			method: 'post',
			url: isDeviceLicense? `${ENDPOINT_URL}${UPLOAD_DEVICE_LICENCE}`: `${ENDPOINT_URL}${UPLOAD_LICENCE_API}`,
			maxBodyLength: Infinity,
			data: uploadData,
		};

		let { data } = await axios.request(config);
		const jsonString = decryptData(data);
		data = JSON.parse(jsonString);
		if (
			data &&
			data.status &&
			data.status === AUTH_TOKEN_ERROR_STATUS_CODE
		) {
			dispatch(logout());
			return false
		}
		if (data?.data && API_SUCCESS_CODES?.includes(data?.status)) {
			dispatch({
				type: UPLOAD_LICENSE_SUCCESS,
                msg: data.msg,
				payload: data.data,
			});
		} else {
			dispatch({
				type: UPLOAD_LICENSE_FAIL,
				payload: data?.errors || UPLOAD_LICENSE_FAIL,
			});
		}
	} catch (error) {
		console.error('Error fetching licenses:', error);
		dispatch({
			type: UPLOAD_LICENSE_FAIL,
			payload: error.message || UPLOAD_LICENSE_FAIL,
		});
	}
};

export const patchLicense = (URL, uploadData) => async (dispatch, getState) => {
	try {
		dispatch({ type: LICENSE_UPDATE_PATCH });

		const { } = getState();

		const config = {
			method: 'patch',
			url: `${ENDPOINT_URL}${URL}`,
			maxBodyLength: Infinity,
			data: uploadData,
		};

		let { data } = await axios.request(config);
		const jsonString = decryptData(data);
		data = JSON.parse(jsonString);
		if (
			data &&
			data.status &&
			data.status === AUTH_TOKEN_ERROR_STATUS_CODE
		) {
			dispatch(logout());
			return false
		}
		if (API_SUCCESS_CODES?.includes(data?.status)) {
			dispatch({
				type: LICENSE_UPDATE_PATCH_SUCCESS,
				payload: data.msg,
			});
		} else {
			dispatch({
				type: LICENSE_UPDATE_PATCH_ERROR,
				payload: data?.errors || LICENSE_UPDATE_PATCH_ERROR,
			});
		}
		return data;
	} catch (error) {
		console.error('Error fetching licenses:', error);
		dispatch({
			type: LICENSE_UPDATE_PATCH_ERROR,
			payload: error.message || LICENSE_UPDATE_PATCH_ERROR,
		});
	}
};

export const postLicense = (URL, uploadData) => async (dispatch, getState) => {
	try {
		dispatch({ type: LICENSE_UPDATE_POST });

		const { } = getState();


		const config = {
			method: 'post',
			url: `${ENDPOINT_URL}${URL}`,
			maxBodyLength: Infinity,
			data: uploadData,
		};

		let { data } = await axios.request(config);
		const jsonString = decryptData(data);
		data = JSON.parse(jsonString);
		if (
			data &&
			data.status &&
			data.status === AUTH_TOKEN_ERROR_STATUS_CODE
		) {
			dispatch(logout());
			return false
		}
		if (API_SUCCESS_CODES?.includes(data?.status)) {
			dispatch({
				type: LICENSE_UPDATE_POST_SUCCESS,
				payload: data.msg,
			});
		} else {
			dispatch({
				type: LICENSE_UPDATE_POST_FAIL,
				payload: data?.errors || LICENSE_UPDATE_POST_FAIL,
			});
		}
		return data;
	} catch (error) {
		console.error('Error fetching licenses:', error);
		dispatch({
			type: LICENSE_UPDATE_POST_FAIL,
			payload: error.message || LICENSE_UPDATE_POST_FAIL,
		});
	}
};

export const apNotification = (uploadData) => async (dispatch, getState) => {
	try {
		dispatch({ type: AP_NOTIFICATION });

		const { } = getState();
		const APPRECOM_BASE_URL = decryptData(localStorage.getItem('aprecom_url'));

		const jsonData = JSON.stringify(uploadData);
		const ciphertext = encryptData(jsonData);

		const apUrl = `${APPRECOM_BASE_URL}:8081/provision/notification`;

		const config = {
			method: 'post',
			url: `${ENDPOINT_URL}${apUrl}`,
			maxBodyLength: Infinity,
			data: ciphertext,
		};

		let { data } = await axios.request(config);
		const jsonString = decryptData(data);
		data = JSON.parse(jsonString);
		if (
			data &&
			data.status &&
			data.status === AUTH_TOKEN_ERROR_STATUS_CODE
		) {
			dispatch(logout());
			return false
		}
		if (data?.data && API_SUCCESS_CODES?.includes(data?.status)) {
			dispatch({
				type: AP_NOTIFICATION_SUCCESS,
				payload: data.data,
			});
		} else {
			dispatch({
				type: AP_NOTIFICATION_FAIL,
				payload: data?.errors || AP_NOTIFICATION_FAIL,
			});
		}
	} catch (error) {
		console.error('Error fetching licenses:', error);
		dispatch({
			type: AP_NOTIFICATION_FAIL,
			payload: error.message || AP_NOTIFICATION_FAIL,
		});
	}
};

/**
 * Fetch license details for showing alert.
 * @return {function} - An asynchronous function that dispatches Redux actions.
*/
export const licenseDetails = () => async (dispatch, getState) => {
    try {
		const { } = getState();

		const config = {
			method: 'get',
			maxBodyLength: Infinity,
			url: ENDPOINT_URL + LICENSE_DETAILS_POPUP,
			};
		dispatch({
			type : LICENSE_DETAILS_POPUP_REQUEST
		})
		let { data } = await axios.request(config);
		const jsonString = decryptData(data);
		data = JSON.parse(jsonString);
		if (
			data &&
			data.status &&
			data.status === AUTH_TOKEN_ERROR_STATUS_CODE
		) {
			dispatch(logout());
			return false
		} else if (
			data &&
			data.status &&
			data.status === API_RESPONSE_SUCCESS_STATUS_CODE
		) {
			dispatch({
				type : LICENSE_DETAILS_POPUP_SUCCESS,
				payload : data?.data
			})
			return true	
		}
		else {
			dispatch({
				type : LICENSE_DETAILS_POPUP_FAILURE,
				payload : 'Something went wrong'
			})
			return false	
		}
	} catch (error) {
		const message =
			error.response && error.response.finalResponse.message
				? error.response.finalResponse.message
				: error.message;
		if (message === TOKEN_FAILED) {
			dispatch(logout());
		}
		dispatch({
			type : LICENSE_DETAILS_POPUP_FAILURE,
			payload : 'Something went wrong'
		})
		return false
	}
};

/**
 * update license details when license expired.
 * @param {*} payload 
 * @return {function} - An asynchronous function that dispatches Redux actions.
*/
export const licenseUpdatePopup = (payload) => async (dispatch, getState) => {
    try {
		const config = {
			method: 'get',
			maxBodyLength: Infinity,
			url: ENDPOINT_URL + LICENSE_DETAILS_UPDATE_POPUP,
			};
		dispatch({
			type : LICENSE_UPDATE_POPUP_REQUEST
		})	
		let { data } = await axios.request(config);
		const jsonString = decryptData(data);
		data = JSON.parse(jsonString);

		console.log('data22',data)

		if (
			data &&
			data.status &&
			data.status === AUTH_TOKEN_ERROR_STATUS_CODE
		) {
			dispatch(logout());
			return false
		} else if (
			data &&
			data.status &&
			data.status === API_RESPONSE_SUCCESS_STATUS_CODE &&
			data?.data
		) {
			dispatch({
				type : LICENSE_UPDATE_POPUP_SUCCESS,
				payload: data.data
			})	
			return true
		}else {
			dispatch({
				type : LICENSE_UPDATE_POPUP_FAILURE,
				payload: 'Something went wrong'
			})
			return false
		}
	} catch (error) {
		const message =
			error.response && error.response.finalResponse.message
				? error.response.finalResponse.message
				: error.message;
		if (message === TOKEN_FAILED) {
			dispatch(logout());
		}
		dispatch({
			type : LICENSE_UPDATE_POPUP_FAILURE,
			payload: 'Something went wrong'
		})
		return false
	}
};

/**
 * update license details when license expired.
 * @param {*} payload 
 * @return {function} - An asynchronous function that dispatches Redux actions.
*/
export const licenseAccountsUpdatePopup = (payload) => async (dispatch, getState) => {
    try {
		const { } = getState();

		const jsonData = JSON.stringify(payload);
		const ciphertext = encryptData(jsonData);
		console.log("Text00",payload);

		const config = {
			method: 'patch',
			maxBodyLength: Infinity,
			url: ENDPOINT_URL + LICENSE_DETAILS_UPDATE_POPUP,data: ciphertext
		};
		dispatch({
			type : LICENSE_UPDATE_POPUP_REQUEST
		})	
		let { data } = await axios.request(config);
		const jsonString = decryptData(data);
		data = JSON.parse(jsonString);

		console.log("data00",data);

		if (
			data &&
			data.status &&
			data.status === AUTH_TOKEN_ERROR_STATUS_CODE
		) {
			dispatch(logout());
			return false
		} else if (
			data &&
			data.status &&
			data.status === API_RESPONSE_SUCCESS_STATUS_CODE &&
			data?.data
		) {
			dispatch({
				type : LICENSE_UPDATE_POPUP_SUCCESS,
				payload: data.data
			})	
			return true
		}else {
			dispatch({
				type : LICENSE_UPDATE_POPUP_FAILURE,
				payload: 'Something went wrong'
			})
			return false
		}
	} catch (error) {
		const message =
			error.response && error.response.finalResponse.message
				? error.response.finalResponse.message
				: error.message;
		if (message === TOKEN_FAILED) {
			dispatch(logout());
		}
		dispatch({
			type : LICENSE_UPDATE_POPUP_FAILURE,
			payload: 'Something went wrong'
		})
		return false
	}
};